import { useEffect, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";

export interface Notificacion {
  id: string;
  tipo: string;
  titulo: string;
  mensaje: string;
  link: string | null;
  icono: string | null;
  leida: boolean;
  leida_en: string | null;
  created_at: string;
}

export function useNotificaciones(userId: string | null) {
  const [notificaciones, setNotificaciones] = useState<Notificacion[]>([]);
  const [noLeidas, setNoLeidas] = useState(0);

  useEffect(() => {
    if (!userId) return;

    // Carga inicial
    supabase
      .from("notificaciones")
      .select("*")
      .eq("destinatario_id", userId)
      .order("created_at", { ascending: false })
      .limit(50)
      .then(({ data }) => {
        if (data) {
          setNotificaciones(data as Notificacion[]);
          setNoLeidas(data.filter((n) => !n.leida).length);
        }
      });

    // Realtime
    const channel = supabase
      .channel(`notif:${userId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notificaciones",
          filter: `destinatario_id=eq.${userId}`,
        },
        (payload) => {
          const n = payload.new as Notificacion;
          setNotificaciones((prev) => [n, ...prev].slice(0, 50));
          setNoLeidas((prev) => prev + 1);
          toast(n.titulo, { description: n.mensaje });
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [userId]);

  async function marcarLeida(id: string) {
    await supabase
      .from("notificaciones")
      .update({ leida: true, leida_en: new Date().toISOString() })
      .eq("id", id);
    setNotificaciones((prev) =>
      prev.map((n) => (n.id === id ? { ...n, leida: true } : n)),
    );
    setNoLeidas((prev) => Math.max(0, prev - 1));
  }

  async function marcarTodasLeidas() {
    if (!userId) return;
    await supabase
      .from("notificaciones")
      .update({ leida: true, leida_en: new Date().toISOString() })
      .eq("destinatario_id", userId)
      .eq("leida", false);
    setNotificaciones((prev) => prev.map((n) => ({ ...n, leida: true })));
    setNoLeidas(0);
  }

  return { notificaciones, noLeidas, marcarLeida, marcarTodasLeidas };
}
